import React from 'react';
import { withRouter, Link, Prompt } from 'react-router-dom';
import { Container, Tabs, Tab } from 'react-bootstrap';
import moment from 'moment';
import queryString from 'query-string';

// Services & Helpers
import BaseService from 'services/BaseService';
import GlobalStateService from 'services/GlobalStateService';
import InstructorService from 'services/InstructorService';
import LocationService from 'services/LocationService';
import TextHelpers from 'helpers/TextHelpers';
import BootboxHelper from 'helpers/BootboxHelper';

// Components
import Loader from 'components/common/Loader';
import EditBookingModal from 'components/bookings/EditBookingModal';
import ReferralCodesModal from 'components/instructor/ReferralCodesModal';
import PageHeader from 'components/common/PageHeader';
import BasicDetails from 'components/instructor/BasicDetails';
import OtherDetails from 'components/instructor/OtherDetails';
import Credentials from 'components/instructor/Credentials';
import Deliveries from 'components/instructor/Deliveries';
import Contract from 'components/instructor/Contract';
import Onboarding from 'components/instructor/Onboarding';
import AuditLogModal from 'components/admin/audit/AuditLogModal';
import ContractModal from 'components/admin/contract/ContractModal';

// Images
import silhouetteImage from 'images/silhouette.png';

//-------------------------------------------------------------------------------------------------------------------

class InstructorViewPage extends React.Component {

    constructor(props) {
        super(props);

        this.bookingsSectionRef = React.createRef();
        this.editBookingModalRef = React.createRef();
        this.referralCodesModalRef = React.createRef();
        this.auditLogModalRef = React.createRef();
        this.contractModalRef = React.createRef();

        this.state = {
            isLoading: true,
            isEditMode: false,
            tab: 'basic-details'
        };
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  React lifecycle
    //-------------------------------------------------------------------------------------------------------------------

    componentDidMount() {
        window.scrollTo(0, 0);
        this.load();
    }

    componentDidUpdate(oldProps) {
        if (this.props.match.params.slug != oldProps.match.params.slug) {
            this.load();
        }
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  General
    //-------------------------------------------------------------------------------------------------------------------

    async load(isSilent) {
        if (!isSilent) {
            this.setState({ isLoading: true });
        }
        this.setState({ isChanged: false, isBusy: false });
        const slug = this.props.match.params.slug;
        const qs = queryString.parse(window.location.search);
        const loginInfo = GlobalStateService.getValue('loginInfo');

        // Load geos and countries
        const geos = await LocationService.listGeos();
        const countriesReq = LocationService.listCountries();

        // Load instructor
        let instructor;
        if (slug == 'new') {
            instructor = {
                type: qs.type,
                status: (qs.type == 'Approved' ? 'Standard' : null),
                activeStatus: 'Pending',
                languages: [],
                courses: [],
                certs: [],
                isAvailableILT: true,
                isAvailableVILT: true,
                onboardDate: moment().startOf('day').toDate(),
                onboardingSteps: {},
                geoIDs: {},
                workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                subAgencyID: (Number(qs.subAgencyID) || null)
            };

            // Set default geo
            geos.map(g => {
                if (g.isDefault) {
                    instructor.geoIDs[g.id] = true;
                    instructor.primaryGeoID = g.id;
                }
            });
        } else {
            instructor = await BaseService.callAPI('instructor/get-basic-details/' + slug);
        }
        GlobalStateService.setValues({
            area: 'instructors-' + (instructor.type || '').toLowerCase()
        });

        // SubAgencyID must be explicitly selected, and 'none' uses value 0
        if (instructor.id && !instructor.subAgencyID) {
            instructor.subAgencyID = 0;
        }

        // Update UI
        this.setState({
            instructor,
            geos,
            countries: await countriesReq,
            loginInfo,
            isLoading: false,
            isEditMode: !loginInfo.isVendor
        });
    }

    updateFields(values) {
        const instructor = { ...this.state.instructor };
        for (var fieldName in values) {
            const value = values[fieldName];
            instructor[fieldName] = value;
        }
        this.setState({ instructor });
    }

    async confirmDelete() {
        const { instructor } = this.state;
        const confirm = await BootboxHelper.confirm('Are you sure you want to delete this instructor?');
        if (confirm) {
            this.delete();
        }
    }

    async delete() {
        const { instructor } = this.state;
        this.setState({ isLoading: true });
        await InstructorService.delete(instructor.id);
        this.props.history.push('/instructors/' + (instructor.type || '').toLowerCase());
    }

    async uploadPhoto(field) {
        const { instructor } = this.state;
        if (field.files.length > 0) {
            const file = field.files[0];
            await InstructorService.uploadPhoto(instructor.id, file);
            field.value = '';
            this.updateFields({
                photoVersion: instructor.photoVersion + 1
            });
        }
    }

    showAuditLog() {
        const { instructor } = this.state;
        this.auditLogModalRef.current.show({
            entityType: 'Instructor',
            entityID: instructor.id
        });
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  Onboarding
    //-------------------------------------------------------------------------------------------------------------------

    async viewContract(id) {
        await this.contractModalRef.current.show({ id });
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  Bookings
    //-------------------------------------------------------------------------------------------------------------------

    async editBooking(booking) {
        await this.editBookingModalRef.current.show({
            bookingID: booking.id,
            instructorID: booking.instructorID,
            status: booking.status
        });
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  Referral codes
    //-------------------------------------------------------------------------------------------------------------------

    showReferralCodesModal() {
        this.referralCodesModalRef.current.show({
            id: this.state.instructor.id
        });
    }

    //-------------------------------------------------------------------------------------------------------------------

    render() {
        const {
            isLoading,
            instructor,
            countries,
            isEditMode,
            geos,
            isChanged,
            onSave,
            isBusy
        } = this.state;
        const { history } = this.props;
        const tab = this.props.match.params.tab || 'basic-details';

        if (isLoading) {
            return (<Loader />);
        }
        const loginInfo = GlobalStateService.getValue('loginInfo');
        
        setTimeout(() => {
            this.autoFocus = null;
        }, 0);

        const tabProps = {
            history,
            slug: instructor.slug,
            loginInfo,
            instructor,
            geos,
            countries,
            isEditMode,
            isChanged,
            onUpdate: (instructor) => this.setState({ instructor }), //this.updateFields,
            setIsChanged: (isChanged, onSave) => {
                window.onbeforeunload = (isChanged ?
                    () => 'You have unsaved changes. Are you sure you want to move away from this page?' :
                    () => null
                );
                this.setState({ isChanged, onSave });
            },
            setIsBusy: (isBusy) => this.setState({ isBusy })
        };

        return (<>

            <Prompt
                when={!!isChanged}
                message={location => 'You have unsaved changes. Are you sure you want to move away from this page?'}
            />

            <div className="page instructor-view-page">

                <PageHeader>
                    {this.renderProfilePhoto()}

                    <h1>
                        {(instructor.firstName || instructor.lastName) ?
                            <>{instructor.firstName} {instructor.lastName}</> :
                            <>New instructor</>
                        }
                        {loginInfo.isVendorOrAdmin && <>
                            {instructor.activeStatus == 'Deactivated' &&
                                <span className="icon fa-solid fa-skull-crossbones" title="Deactivated" />
                            }
                            {instructor.activeStatus == 'Suspended' &&
                                <span className="icon fa-solid fa-bed" title="Suspended" />
                            }
                            {instructor.isPriority &&
                                <span className="icon fa-solid fa-star" title="Priority" />
                            }
                            {instructor.isAvailableILT &&
                                <span className="icon fa-solid fa-building-user" title="Available to teach ILT" />
                            }
                        </>}
                    </h1>

                    {!!instructor.id &&
                        <div className="buttons">

                            {this.renderActionsButton()}
                            
                        </div>
                    }

                </PageHeader>

                <main>

                    <Container>

                        <Tabs
                            activeKey={tab}
                            onSelect={tab => {
                                this.props.history.replace(`/instructor/${instructor.slug}/${tab}`);
                            }}
                        >
                            <Tab eventKey="basic-details" title="Basic Details">
                                {tab == 'basic-details' &&
                                    <BasicDetails {...tabProps} />
                                }
                            </Tab>
                            {instructor.id && instructor.onboardingStatus &&
                                <Tab eventKey="onboarding" title="Onboarding">
                                    {tab == 'onboarding' &&
                                        <Onboarding {...tabProps} />
                                    }
                                </Tab>
                            }
                            {instructor.type == 'Approved' && !!instructor.id &&
                                <Tab eventKey="deliveries" title="Deliveries">
                                    {tab == 'deliveries' &&
                                        <Deliveries {...tabProps} />
                                    }
                                </Tab>
                            }
                            {instructor.type == 'Approved' && !!instructor.id &&
                                <Tab eventKey="credentials" title="Credentials">
                                    {tab == 'credentials' &&
                                        <Credentials {...tabProps} />
                                    }
                                </Tab>
                            }
                            {loginInfo.isAdmin && !!instructor.id &&
                                <Tab eventKey="contract" title="Contract">
                                    {tab == 'contract' &&
                                        <Contract {...tabProps} />
                                    }
                                </Tab>
                            }
                            {(loginInfo.isAdmin || instructor.isAvailableILT) && !!instructor.id &&
                                <Tab eventKey="other" title="Other">
                                    {tab == 'other' &&
                                        <OtherDetails {...tabProps} />
                                    }
                                </Tab>
                            }
                        </Tabs>

                    </Container>

                </main>

            </div>

            {isChanged && !isBusy &&
                <button type="button" className="btn btn-primary floating-save-button" onClick={onSave}>
                    <span className="fa-solid fa-check" />{' '}
                    Save changes
                </button>
            }

            <EditBookingModal ref={this.editBookingModalRef} />
            <ReferralCodesModal ref={this.referralCodesModalRef} />
            <AuditLogModal ref={this.auditLogModalRef} />
            <ContractModal ref={this.contractModalRef} />
            
        </>);
    }

    renderProfilePhoto() {
        const { isEditMode, instructor } = this.state;

        return (
            <label className={`profile-photo upload-button ${isEditMode && instructor.id ? 'selectable' : ''}`} style={{
                backgroundImage: (
                    instructor.photoVersion ? `url(/api/instructor/get-photo/${instructor.id}?size=Medium&v=${instructor.photoVersion})` :
                        `url(${silhouetteImage})`
                )
            }}>
                {instructor.id && isEditMode && <>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={e => this.uploadPhoto(e.target)}
                    />
                    <div className="edit-icon">
                        <span className="fa-solid fa-pencil-alt"></span>
                    </div>
                </>}
            </label>
        );
    }

    renderActionsButton() {
        const { instructor } = this.state;
        const loginInfo = GlobalStateService.getValue('loginInfo');
        
        if (loginInfo.isAdmin) {
            return (
                <div className="dropdown">
                    <button className="btn btn-primary dropdown-toggle" type="button" id="actions-menu" data-bs-toggle="dropdown" aria-expanded="false">
                        <span className="d-none d-sm-inline">Actions</span>
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="actions-menu">

                        {!!instructor.id && <>

                            {!!instructor.latestContractID &&
                                <li>
                                    <Link className="dropdown-item" to="#" onClick={e => {
                                        e.preventDefault();
                                        this.viewContract(instructor.latestContractID);
                                    }}>
                                        <span className="fa-solid fa-handshake"></span>{' '}
                                        Contract
                                    </Link>
                                </li>
                            }

                            {instructor.type == 'Approved' &&
                                <li>
                                    <Link className="dropdown-item" to={'/availability-selector/' + (loginInfo.isInstructor ? '' : instructor.slug)}>
                                        <span className="fa-solid fa-calendar-alt"></span>{' '}
                                        Edit availability
                                    </Link>
                                </li>
                            }

                            {instructor.type == 'Approved' &&
                                <li>
                                    <a className="dropdown-item" href="#" onClick={async e => {
                                        e.preventDefault();
                                        await this.editBooking({
                                            instructorID: instructor.id,
                                            status: 'Hold'
                                        });
                                        this.bookingsSectionRef.current.loadYears();
                                    }}>
                                        <span className="fa-solid fa-plus"></span>{' '}
                                        Add booking
                                    </a>
                                </li>
                            }

                            {loginInfo.site.enableReferralCodes &&
                                <li>
                                    <a className="dropdown-item" href="#" onClick={e => {
                                        e.preventDefault();
                                        this.showReferralCodesModal();
                                    }}>
                                        <span className="far fa-handshake"></span>{' '}
                                        Referral Codes
                                    </a>
                                </li>
                            }
                            
                            <li>
                                <a className="dropdown-item" href="#" onClick={e => {
                                    e.preventDefault();
                                    this.showAuditLog();
                                }}>
                                    <span className="fa fa-magnifying-glass"></span>{' '}
                                    Audit log
                                </a>
                            </li>

                            <li>
                                <a className="dropdown-item" href="#" onClick={e => {
                                    e.preventDefault();
                                    this.confirmDelete();
                                }}>
                                    <span className="fa-solid fa-trash"></span>
                                    {' '}
                                    Delete
                                </a>
                            </li>
                            
                        </>}
                        
                        {!instructor.id &&
                            <li>
                                <Link className="dropdown-item" to="#" onClick={e => {
                                    e.preventDefault();
                                    this.save();
                                }}>
                                    <span className="fa-solid fa-save"></span>{' '}
                                    Save
                                </Link>
                            </li>
                        }

                        {instructor.mobileNumber &&
                            <li>
                                <a className="dropdown-item" href={'https://web.whatsapp.com/send?phone=' + TextHelpers.getPhoneNumberNumeric(instructor.mobileNumber) + '&text&app_absent=0'} target="_blank">
                                    <span className="fab fa-whatsapp"></span>{' '}
                                    WhatsApp
                                </a>
                            </li>
                        }

                        {instructor.email &&
                            <li>
                                <a className="dropdown-item" href={'mailto:' + instructor.email} target="_blank">
                                    <span className="fa-solid fa-envelope"></span>{' '}
                                    Email
                                </a>
                            </li>
                        }
                    </ul>
                </div>
            );
        } else if (!loginInfo.isManager) {
            return (
                <div className="dropdown">
                    <button className="btn btn-primary dropdown-toggle" type="button" id="actions-menu" data-bs-toggle="dropdown" aria-expanded="false">
                        <span className="d-none d-sm-inline">Actions...</span>
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="actions-menu">
                        
                        <li>
                            <a className="dropdown-item" href="#" onClick={e => {
                                e.preventDefault();
                                this.showReferralCodesModal();
                            }}>
                                <span className="far fa-handshake"></span>{' '}
                                Referral codes
                            </a>
                        </li>
                        
                    </ul>
                </div>
            );
        }
    }

}

export default withRouter(InstructorViewPage);